import Head from "next/head"
import * as Sentry from "@sentry/node"
import { AppProps } from "next/app"
import { useRouter } from "next/router"
import { LanguageOption, userPool } from "@project/shared"
import React, { useEffect, useState } from "react"
import { message } from "antd"

import { CloseCircleFilled } from "@ant-design/icons"
import { useTranslation } from "react-i18next"
import { QueryClient, QueryClientProvider } from "react-query"
import { GlobalStyles, AuthProvider } from "../utils"
import "../utils/css-imports"
import { AdminType } from "../utils/enums/adminType"

export const queryClient = new QueryClient({ defaultOptions: {} })

if (process.env.NEXT_PUBLIC_SENTRY_DSN) {
  Sentry.init({
    enabled:
      process.env.ENVIRONMENT === "development" ||
      process.env.ENVIRONMENT === "production",
    environment: `admin-${process.env.NODE_ENV}`,
    dsn: process.env.NEXT_PUBLIC_SENTRY_DSN,
  })
}

const MyApp = ({ Component, pageProps }: AppProps) => {
  const [loading, setLoading] = useState(true)
  const [user, setUser] = useState(null)
  const [isAdmin, setIsAdmin] = useState(false)
  const [isSuperAdmin, setIsSuperAdmin] = useState(false)
  const { t } = useTranslation()

  const checkLoggedInUser = (payload: any) => {
    if (
      payload["cognito:groups"]?.[0] === AdminType.SUPER_ADMIN ||
      payload["cognito:groups"]?.[0] === AdminType.ADMIN_USER
    ) {
      setUser(payload)
      if (payload["cognito:groups"]?.[0] === AdminType.SUPER_ADMIN) {
        setIsSuperAdmin(true)
      } else {
        setIsAdmin(true)
      }
    } else {
      setUser(null)
      localStorage.clear()
      message.error({
        key: "01",
        icon: <CloseCircleFilled onClick={() => message.destroy("01")} />,
        content: t("Unauthorized user"),
      })
    }
  }

  const initialLoad = () => {
    try {
      const user = userPool.getCurrentUser()
      if (user) {
        user.getSession(
          (
            err: any,
            session: { isValid: () => any; getIdToken: () => any }
          ) => {
            if (!err && session.isValid()) {
              const idTokenPayload = session.getIdToken().payload
              checkLoggedInUser(idTokenPayload)
            }
          }
        )
      }
      setLoading(false)
    } catch (error) {
      setUser(null)
      setLoading(false)
      Sentry.captureException(error)
      message.error({
        key: "02",
        content: t("An error has occurred. Please try again later."),
        icon: <CloseCircleFilled onClick={() => message.destroy("02")} />,
      })
    }
  }

  useEffect(() => {
    initialLoad()
  }, [])

  return (
    <React.Fragment>
      <Head>
        <link
          rel={"icon"}
          type={"image/svg+xml"}
          href={"/assets/icons/logo.svg"}
        />
        <title>{"管理サイト"}</title>
      </Head>
      <GlobalStyles />
      <QueryClientProvider client={queryClient}>
        <AuthProvider
          loading={loading}
          user={user}
          isAdmin={isAdmin}
          setIsAdmin={setIsAdmin}
          setUser={setUser}
          isSuperAdmin={isSuperAdmin}
          setIsSuperAdmin={setIsSuperAdmin}
        >
          <Component {...pageProps} />
          {process.env.NEXT_PUBLIC_ENVIRONMENT != "production" && (
            <LanguageOption />
          )}
        </AuthProvider>
      </QueryClientProvider>
    </React.Fragment>
  )
}

export default MyApp
